import React, { useEffect, useMemo, useState } from "react";
import { getAuth } from "firebase/auth";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import {
  createTenantSubscriptionCheckout,
  previewTenantSubscriptionPlanChange,
  updateTenantSubscriptionPlan,
  createTenantBillingPortal,
  cancelTenantBilling,
} from "../lib/tenantBillingApi";

const styles = {
  section: {
    marginTop: 24,
    paddingTop: 20,
    borderTop: "1px solid #e8ebf3",
  },
  layout: {
    display: "grid",
    gridTemplateColumns: "220px 1fr",
    gap: "16px 20px",
    alignItems: "start",
  },
  heading: {
    margin: 0,
    fontSize: 16,
    fontWeight: 700,
    color: "#2a0516",
  },
  helpText: {
    margin: "6px 0 0 0",
    fontSize: 14,
    lineHeight: 1.5,
    color: "#4b5563",
  },
  actionsWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
    alignItems: "center",
  },
  buttonBase: {
    appearance: "none",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 14,
    fontWeight: 700,
    cursor: "pointer",
    transition: "all 0.18s ease",
    outline: "none",
  },
  buttonPrimary: {
    background: "#2a0516",
    color: "#ffffff",
    border: "1px solid #2a0516",
    boxShadow: "0 4px 14px rgba(42,5,22,0.12)",
  },
  buttonSecondary: {
    background: "#ffffff",
    color: "#2a0516",
    border: "1px solid #d7dce8",
  },
  buttonDanger: {
    background: "#ffffff",
    color: "#b42318",
    border: "1px solid #f0b7b2",
  },
  buttonCurrent: {
    background: "#f4f6fb",
    color: "#657899",
    border: "1px solid #d7dce8",
  },
  disabled: {
    opacity: 0.55,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  note: {
    marginTop: 12,
    fontSize: 13,
    color: "#6b7280",
  },
  errorBox: {
    marginTop: 12,
    border: "1px solid #f3c7c4",
    background: "#fff5f4",
    color: "#b42318",
    borderRadius: 10,
    padding: "10px 12px",
    fontSize: 14,
    lineHeight: 1.45,
  },
  previewCard: {
    marginTop: 16,
    border: "1px solid #e8ebf3",
    borderRadius: 14,
    background: "#fafbff",
    padding: 16,
  },
  previewTitle: {
    margin: 0,
    fontSize: 15,
    fontWeight: 700,
    color: "#2a0516",
  },
  previewText: {
    margin: "8px 0 0 0",
    fontSize: 14,
    color: "#4b5563",
    lineHeight: 1.55,
  },
  previewGrid: {
    marginTop: 14,
    display: "grid",
    gridTemplateColumns: "220px 1fr",
    gap: "10px 16px",
  },
  previewActions: {
    marginTop: 16,
    display: "flex",
    flexWrap: "wrap",
    gap: 10,
  },
};

function buttonStyle(kind, disabled) {
  const base = { ...styles.buttonBase };
  const variant =
    kind === "primary"
      ? styles.buttonPrimary
      : kind === "danger"
      ? styles.buttonDanger
      : kind === "current"
      ? styles.buttonCurrent
      : styles.buttonSecondary;

  return {
    ...base,
    ...variant,
    ...(disabled ? styles.disabled : {}),
  };
}

function formatMoney(amountMinor = 0, currency = "EUR") {
  const amount = Number(amountMinor || 0) / 100;

  try {
    return new Intl.NumberFormat("en-IE", {
      style: "currency",
      currency: String(currency || "EUR").toUpperCase(),
    }).format(amount);
  } catch (e) {
    return `${amount.toFixed(2)} ${String(currency || "EUR").toUpperCase()}`;
  }
}

function formatDateValue(value) {
  if (!value) return "—";

  try {
    if (typeof value?.toDate === "function") {
      return value.toDate().toLocaleDateString();
    }
    if (typeof value?.seconds === "number") {
      return new Date(value.seconds * 1000).toLocaleDateString();
    }
    if (typeof value === "number") {
      return new Date(value * 1000).toLocaleDateString();
    }
    if (typeof value === "string") {
      return value;
    }
  } catch (e) {}

  return "—";
}

function getPlanRank(planKey) {
  const plan = String(planKey || "").toLowerCase();
  if (plan === "starter") return 1;
  if (plan === "growth") return 2;
  if (plan === "custom") return 3;
  return 0;
}

export default function TenantBillingActions({ tenantId, currentPlan, hasStripeSubscription }) {
  const [loadingRole, setLoadingRole] = useState(true);
  const [canManageBilling, setCanManageBilling] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [isCompact, setIsCompact] = useState(false);
  const [preview, setPreview] = useState(null);

  const normalizedPlan = useMemo(
    () => String(currentPlan || "").trim().toLowerCase(),
    [currentPlan]
  );

  const previewDirection = useMemo(() => {
    if (!preview?.currentPlanKey || !preview?.targetPlanKey) return null;
    return getPlanRank(preview.targetPlanKey) > getPlanRank(preview.currentPlanKey)
      ? "upgrade"
      : "downgrade";
  }, [preview]);

  const previewMessage = useMemo(() => {
    if (!preview?.ok || preview?.noChange) return "";

    const recurring = formatMoney(preview.recurringAmount, preview.currency);
    const renewalDate = formatDateValue(preview.nextRenewalDate);

    if (previewDirection === "upgrade") {
      return `If you confirm this upgrade, Stripe will apply the plan change immediately and any prorated charge or adjustment. Your new recurring monthly price will be ${recurring} and the next renewal date is ${renewalDate}.`;
    }

    if (previewDirection === "downgrade") {
      return `If you confirm this downgrade, it will be scheduled for your next renewal date. Your current plan will remain active until then. The new recurring monthly price from ${renewalDate} will be ${recurring}.`;
    }

    return `Please review the Stripe billing preview before confirming this subscription change.`;
  }, [preview, previewDirection]);

  useEffect(() => {
    function handleResize() {
      setIsCompact(window.innerWidth < 900);
    }

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadPermissions() {
      try {
        setLoadingRole(true);
        setError("");

        const auth = getAuth();
        const db = getFirestore();
        const user = auth.currentUser;

        if (!user || !tenantId) {
          if (mounted) {
            setCanManageBilling(false);
            setLoadingRole(false);
          }
          return;
        }

        const tokenResult = await user.getIdTokenResult();
        const claims = tokenResult?.claims || {};

        if (claims.admin === true) {
          if (mounted) {
            setCanManageBilling(true);
            setLoadingRole(false);
          }
          return;
        }

        const userSnap = await getDoc(doc(db, "users", user.uid));
        const userData = userSnap.exists() ? (userSnap.data() || {}) : {};

        if (
          userData.isAdmin === true ||
          String(userData.role || "").toLowerCase() === "platform_admin"
        ) {
          if (mounted) {
            setCanManageBilling(true);
            setLoadingRole(false);
          }
          return;
        }

        const memberSnap = await getDoc(doc(db, "tenants", tenantId, "members", user.uid));
        const memberData = memberSnap.exists() ? (memberSnap.data() || {}) : {};
        const tenantRole = String(memberData.role || "").toLowerCase();

        if (mounted) {
          setCanManageBilling(["owner", "hr_admin"].includes(tenantRole));
          setLoadingRole(false);
        }
      } catch (err) {
        console.error("Failed to load billing permissions", err);
        if (mounted) {
          setCanManageBilling(false);
          setLoadingRole(false);
        }
      }
    }

    loadPermissions();

    return () => {
      mounted = false;
    };
  }, [tenantId]);

  async function handlePlan(planKey) {
    if (String(planKey).toLowerCase() === normalizedPlan) {
      return;
    }

    try {
      setWorking(true);
      setError("");
      setPreview(null);

      if (hasStripeSubscription) {
        const result = await previewTenantSubscriptionPlanChange(tenantId, planKey);
        if (result?.ok) {
          setPreview(result);
          return;
        }
        throw new Error("Unable to preview plan change");
      }

      const result = await createTenantSubscriptionCheckout(tenantId, planKey);
      if (result?.url) {
        window.location.href = result.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to update tenant billing");
    } finally {
      setWorking(false);
    }
  }

  async function handleConfirmPreview() {
    if (!preview?.targetPlanKey) return;

    try {
      setWorking(true);
      setError("");

      const result = await updateTenantSubscriptionPlan(
        tenantId,
        preview.targetPlanKey,
        preview.prorationDate || null
      );

      if (result?.ok) {
        if (result.scheduled) {
          window.alert(`Plan change scheduled. The tenant will move to ${preview.targetPlanKey} on the next renewal date.`);
        } else {
          window.alert(`Plan updated to ${preview.targetPlanKey}.`);
        }
        window.location.reload();
        return;
      }

      throw new Error("Plan update failed");
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to confirm plan change");
    } finally {
      setWorking(false);
    }
  }

  function handleCancelPreview() {
    setPreview(null);
    setError("");
  }

  async function handlePortal() {
    try {
      setWorking(true);
      setError("");
      const result = await createTenantBillingPortal(tenantId);
      if (result?.url) {
        window.location.href = result.url;
      } else {
        throw new Error("No billing portal URL returned");
      }
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to open billing portal");
    } finally {
      setWorking(false);
    }
  }

  async function handleCancelAtPeriodEnd() {
    try {
      setWorking(true);
      setError("");
      await cancelTenantBilling(tenantId, false);
      window.alert("Subscription will cancel at period end.");
      window.location.reload();
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to cancel subscription");
    } finally {
      setWorking(false);
    }
  }

  const starterIsCurrent = normalizedPlan === "starter";
  const growthIsCurrent = normalizedPlan === "growth";

  const starterDisabled = loadingRole || working || !canManageBilling || starterIsCurrent;
  const growthDisabled = loadingRole || working || !canManageBilling || growthIsCurrent;
  const portalDisabled = loadingRole || working || !canManageBilling;
  const cancelDisabled = loadingRole || working || !canManageBilling;

  return (
    <div style={styles.section}>
      <div
        style={{
          ...styles.layout,
          gridTemplateColumns: isCompact ? "1fr" : styles.layout.gridTemplateColumns,
        }}
      >
        <div>
          <h4 style={styles.heading}>Billing Actions</h4>
          <p style={styles.helpText}>
            {hasStripeSubscription
              ? "Preview the billing impact before confirming a plan change, open the billing portal, or cancel at period end."
              : "Start a Stripe subscription, open the billing portal, or cancel at period end."}
          </p>
        </div>

        <div>
          <div style={styles.actionsWrap}>
            <button
              type="button"
              disabled={starterDisabled}
              onClick={() => handlePlan("starter")}
              style={buttonStyle(starterIsCurrent ? "current" : "secondary", starterDisabled)}
            >
              {starterIsCurrent ? "Starter (Current)" : "Starter"}
            </button>

            <button
              type="button"
              disabled={growthDisabled}
              onClick={() => handlePlan("growth")}
              style={buttonStyle(growthIsCurrent ? "current" : "secondary", growthDisabled)}
            >
              {growthIsCurrent ? "Growth (Current)" : "Growth"}
            </button>

            <button
              type="button"
              disabled={portalDisabled}
              onClick={handlePortal}
              style={buttonStyle("primary", portalDisabled)}
            >
              Manage Billing
            </button>

            <button
              type="button"
              disabled={cancelDisabled}
              onClick={handleCancelAtPeriodEnd}
              style={buttonStyle("danger", cancelDisabled)}
            >
              Cancel at Period End
            </button>
          </div>

          {!loadingRole && !canManageBilling ? (
            <div style={styles.note}>
              You do not have permission to manage billing for this tenant.
            </div>
          ) : null}

          {preview?.ok && !preview?.noChange ? (
            <div style={styles.previewCard}>
              <h5 style={styles.previewTitle}>Confirm Plan Change</h5>
              <p style={styles.previewText}>{previewMessage}</p>

              <div style={styles.previewGrid}>
                <strong>Current Plan</strong>
                <span style={{ textTransform: "capitalize" }}>{preview.currentPlanKey || "—"}</span>

                <strong>New Plan</strong>
                <span style={{ textTransform: "capitalize" }}>{preview.targetPlanKey || "—"}</span>

                <strong>Estimated Due on Confirmation</strong>
                <span>{formatMoney(preview.amountDueNow, preview.currency)}</span>

                <strong>Stripe Proration Adjustment</strong>
                <span>{formatMoney(preview.prorationAmount, preview.currency)}</span>

                <strong>New Monthly Price</strong>
                <span>{formatMoney(preview.recurringAmount, preview.currency)}</span>

                <strong>Next Renewal Date</strong>
                <span>{formatDateValue(preview.nextRenewalDate)}</span>
              </div>

              <div style={styles.previewActions}>
                <button
                  type="button"
                  disabled={working}
                  onClick={handleConfirmPreview}
                  style={buttonStyle("primary", working)}
                >
                  Confirm Plan Change
                </button>

                <button
                  type="button"
                  disabled={working}
                  onClick={handleCancelPreview}
                  style={buttonStyle("secondary", working)}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}

          {error ? <div style={styles.errorBox}>{error}</div> : null}
        </div>
      </div>
    </div>
  );
}
